import { Link } from '@tanstack/react-router';
import { BookOpen, ChevronDown, LogOut, Moon, Rows3, Sun, UserRound } from 'lucide-react';
import { useTheme } from 'next-themes';
import { type ComponentType, type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { useSession } from '@/components/auth/session';
import { useDensity } from '@/components/layout/DensityProvider';
import { initials } from '@/lib/format';
import { cn } from '@/lib/utils';

/** One line, an icon, a label. What every item in the menu below is built from. */
function MenuItem({
  icon: Icon,
  children,
  hint,
  tone = 'default',
  onClick,
  href,
  onNavigate,
}: {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  children: ReactNode;
  hint?: string;
  tone?: 'default' | 'danger';
  onClick?: () => void;
  href?: string;
  onNavigate?: () => void;
}) {
  const className = cn(
    'flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-left text-sm outline-none',
    tone === 'danger'
      ? 'text-bad hover:bg-bad-bg focus-visible:bg-bad-bg'
      : 'text-fg-2 hover:bg-raised hover:text-foreground focus-visible:bg-raised focus-visible:text-foreground',
  );
  const content = (
    <>
      <Icon className="size-4 shrink-0 opacity-70" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint ? <kbd className="dy-kbd shrink-0">{hint}</kbd> : null}
    </>
  );
  if (href) {
    return (
      <Link
        to={href}
        role="menuitem"
        onClick={() => {
          onClick?.();
          onNavigate?.();
        }}
        className={className}
      >
        {content}
      </Link>
    );
  }
  return (
    <button type="button" role="menuitem" onClick={onClick} className={className}>
      {content}
    </button>
  );
}

function MenuDivider() {
  return <hr className="border-line my-1 border-t" />;
}

/**
 * The foot of the rail: the one place in the console that names who you are,
 * and the one place that holds the preferences that are yours.
 *
 * The row is one line, the same shape as a `NavRow` above it: 36px, one 14px
 * name, a chevron. It used to also carry an 11px role caption and four
 * unlabelled ghost icon buttons underneath -- the only stack of two type sizes
 * and the only row of unlabelled verbs in the rail. Both live in the menu the
 * row opens instead: identity in full (the address a 200px column had to
 * truncate), then the account page, then the two preferences that are yours,
 * then how the product works, then sign out on its own below a divider so the
 * verb that ends the session is never a click away from a toggle.
 *
 * It is a region rather than the tail of `SidebarBody` because the nav above it
 * scrolls and this does not. On a short window the last nav row comes to rest
 * against the account row, which is the one case `DESIGN.md` spends a hairline
 * on: two things that genuinely touch.
 */
export function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { session, signOut } = useSession();
  const { density, toggleDensity } = useDensity();
  const { resolvedTheme, setTheme } = useTheme();
  const name = session.user?.name || session.actor;
  const email = session.user?.email;
  const roleLabel = session.isAdmin ? 'admin' : session.role;

  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  // Focus the first item on open so the arrows have somewhere to start.
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const move = (from: HTMLElement, delta: number) => {
    const items = [...(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const index = items.indexOf(from);
    items[Math.max(0, Math.min(items.length - 1, index + delta))]?.focus();
  };

  const nextTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
  const nextDensity = density === 'compact' ? 'roomy' : 'compact';

  return (
    <div ref={wrap} className="border-line/70 relative shrink-0 border-t p-2.5">
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        title={email ? `${email} · ${roleLabel}` : roleLabel}
        className={cn(
          'group text-foreground hover:bg-raised relative flex h-9 w-full items-center gap-2.5',
          'rounded-lg px-2 text-base transition-colors duration-100',
          open && 'bg-raised',
        )}
      >
        <span
          className="grid size-6 shrink-0 place-items-center rounded-full text-2xs font-semibold text-white"
          style={{ background: 'var(--dy-grad)' }}
          aria-hidden
        >
          {initials(name)}
        </span>
        <span className="min-w-0 flex-1 truncate text-left font-medium">{name}</span>
        <ChevronDown
          className={cn(
            'text-fg-3 size-3.5 shrink-0 transition-transform duration-150',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {open ? (
        <div
          id={menuId}
          ref={list}
          role="menu"
          aria-label="Account"
          className={cn(
            'bg-pop dy-pop-in absolute right-2.5 bottom-[calc(100%+6px)] left-2.5 z-40',
            'rounded-lg border border-line-2 p-1 shadow-pop',
          )}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setOpen(false);
              trigger.current?.focus();
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(event.target as HTMLElement, 1);
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(event.target as HTMLElement, -1);
            }
          }}
        >
          <div className="px-2 py-1.5">
            <p className="text-foreground truncate text-sm font-medium" title={name}>
              {name}
            </p>
            {email ? (
              <p className="text-fg-3 truncate text-2xs" title={email}>
                {email}
              </p>
            ) : null}
          </div>

          <MenuDivider />

          <MenuItem icon={UserRound} href="/me" onClick={close} onNavigate={onNavigate}>
            Your account
          </MenuItem>

          <MenuDivider />

          <MenuItem
            icon={resolvedTheme === 'dark' ? Sun : Moon}
            onClick={() => setTheme(nextTheme)}
          >
            Switch to {nextTheme} theme
          </MenuItem>
          <MenuItem icon={Rows3} hint="d" onClick={toggleDensity}>
            Switch to {nextDensity} density
          </MenuItem>
          <MenuItem icon={BookOpen} href="/about" onClick={close} onNavigate={onNavigate}>
            How Dyspatch works
          </MenuItem>

          <MenuDivider />

          <MenuItem
            icon={LogOut}
            tone="danger"
            onClick={() => {
              close();
              void signOut();
            }}
          >
            Sign out
          </MenuItem>
        </div>
      ) : null}
    </div>
  );
}
